import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dumbbell, Search, Filter, Clock, Zap, ChevronRight, Lock, Play, Star, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { WorkoutDetailModal } from "@/components/athlete/AthleteComponents";
import { useViewPreferences } from "@/components/coach/SharedComponents";

const WorkoutsPage = () => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", difficulty: "" });
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { viewMode, setViewMode } = useViewPreferences("athlete_workouts");

  useEffect(() => {
    fetchWorkouts();
  }, [filter]);

  const fetchWorkouts = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append("category", filter.category);
      if (filter.difficulty) params.append("difficulty", filter.difficulty);

      const response = await fetch(`${API}/workouts/?${params}`, {
        credentials: "include"
      });
      const data = await response.json();
      setWorkouts(data);
    } catch (error) {
      toast.error("Failed to load workouts");
    } finally {
      setLoading(false);
    }
  };

  // Lowercase values to match what WorkoutForm.js writes to the DB
  // (category is stored as a lowercase string like "strength", "speed" …).
  // The `capitalize` class on each option handles display-side casing.
  const categories = ["speed", "strength", "stamina", "suppleness", "skill", "extras"];
  const difficulties = ["beginner", "intermediate", "advanced"];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-start justify-between"
          >
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                WORKOUT LIBRARY
              </h1>
              <p className="text-text-secondary">
                Choose from our curated collection of workouts
              </p>
            </div>
          </motion.div>

          {/* Filters + View Toggle */}
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="bg-surface border border-border rounded-lg px-4 py-2 text-white"
              data-testid="filter-category"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat} className="capitalize">{cat}</option>
              ))}
            </select>
            <select
              value={filter.difficulty}
              onChange={(e) => setFilter({ ...filter, difficulty: e.target.value })}
              className="bg-surface border border-border rounded-lg px-4 py-2 text-white"
              data-testid="filter-difficulty"
            >
              <option value="">All Difficulties</option>
              {difficulties.map(diff => (
                <option key={diff} value={diff} className="capitalize">{diff}</option>
              ))}
            </select>
            <div className="ml-auto flex gap-1 bg-surface border border-border rounded-lg p-1">
              {[{ id: "list", icon: List }, { id: "grid", icon: LayoutGrid }].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setViewMode(opt.id)}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === opt.id ? "bg-primary text-white" : "text-gray-500 hover:text-white"}`}
                  data-testid={`view-${opt.id}`}
                >
                  <opt.icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>

          {/* Workouts Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : workouts.length > 0 ? (
            viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workouts.map((workout, i) => (
                <motion.div
                  key={workout.workout_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-surface border rounded-xl overflow-hidden ${
                    workout.locked ? "border-border opacity-75" : "border-border hover:border-primary/50"
                  } transition-all cursor-pointer`}
                  onClick={() => !workout.locked && setSelectedWorkout(workout)}
                  data-testid={`workout-card-${workout.workout_id}`}
                >
                  <div className="aspect-video bg-[#1E293B] relative">
                    {workout.thumbnail_url ? (
                      <img src={workout.thumbnail_url} alt={workout.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Dumbbell className="w-12 h-12 text-text-muted" />
                      </div>
                    )}
                    {workout.locked && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Lock className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-sm">
                      {workout.duration_minutes} min
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-wider text-primary bg-primary/20 px-2 py-1 rounded">
                        {workout.category}
                      </span>
                      <span className="text-xs uppercase tracking-wider text-text-muted">
                        {workout.times_per_week ? `${workout.times_per_week}x/wk` : ""}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2">{workout.title}</h3>
                    <p className="text-text-secondary text-sm line-clamp-2">{workout.description}</p>
                    {workout.locked && (
                      <p className="text-accent text-sm mt-2">
                        Requires {workout.tier_required} tier
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
            ) : (
            <div className="space-y-2">
              {workouts.map((workout, i) => (
                <motion.div
                  key={workout.workout_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className={`bg-surface border rounded-xl p-4 flex items-center gap-4 ${
                    workout.locked ? "border-border opacity-75" : "border-border hover:border-primary/50"
                  } transition-all cursor-pointer`}
                  onClick={() => !workout.locked && setSelectedWorkout(workout)}
                  data-testid={`workout-card-${workout.workout_id}`}
                >
                  <div className="w-14 h-14 bg-[#1E293B] rounded-lg flex items-center justify-center flex-shrink-0 relative">
                    {workout.locked ? (
                      <Lock className="w-5 h-5 text-text-muted" />
                    ) : (
                      <Dumbbell className="w-6 h-6 text-text-muted" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] uppercase tracking-wider text-primary bg-primary/20 px-1.5 py-0.5 rounded">
                        {workout.category}
                      </span>
                      {workout.times_per_week && (
                        <span className="text-[10px] text-text-muted">{workout.times_per_week}x/wk</span>
                      )}
                    </div>
                    <h3 className="font-bold text-white truncate">{workout.title}</h3>
                    <p className="text-text-secondary text-xs truncate">{workout.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-text-muted">{workout.duration_minutes} min</span>
                    <ChevronRight className="w-4 h-4 text-text-muted" />
                  </div>
                </motion.div>
              ))}
            </div>
            )
          ) : (
            <div className="text-center py-20">
              <Dumbbell className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary text-lg">No workouts assigned yet</p>
              <p className="text-text-muted">Start a program to get access to workouts, or generate one with AI!</p>
              <div className="flex gap-4 justify-center mt-6">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-surface border border-border text-white px-6 py-2 rounded-full hover:bg-[#1E293B] transition-colors"
                >
                  View Programs
                </button>
                <button
                  onClick={() => navigate("/ai-generator")}
                  className="bg-primary text-white px-6 py-2 rounded-full hover:bg-blue-600 transition-colors"
                >
                  Generate Workout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Workout Detail Modal */}
      <AnimatePresence>
        {selectedWorkout && (
          <WorkoutDetailModal
            workout={selectedWorkout}
            onClose={() => setSelectedWorkout(null)}
            onStart={() => {
              setSelectedWorkout(null);
              toast.success("Workout started! Track your progress.");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};


export default WorkoutsPage;
